import { useState, useRef, useCallback, forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { layers } from 'app/spacing';
import { BasicRichTextEditor, defaultFonts } from 'components/WYSIWYG';

import { MaybeInputControl } from '../InputControl';
import { useQueryClient } from '@tanstack/react-query';
import { getTeamMemberSuggestionOptions } from 'components/WYSIWYG/suggestions/team-member';

export { parseMentions } from './utils';

export const useNotesRichTextEditor = (intialState = '') => {
  const editorRef = useRef(null);
  const [state, setState] = useState(intialState);

  const handleNoteChange = useCallback(
    ({ editor }) => {
      setState(editorRef.current.isEmpty() ? '' : editor.getHTML());
    },
    [setState]
  );

  const clearNote = useCallback(() => {
    setState('');
    if (editorRef?.current) {
      const { editor } = editorRef.current;
      editor.commands.clearContent();
    }
  }, [setState]);

  return { notes: state, handleNoteChange, clearNote, editorRef };
};

export const NotesRichTextEditor = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const defaultPlaceholder = t('Enter Note');
  const {
    value,
    onChange,
    autoFocus,
    disabled = false,
    placeholder: placeHolderProp,
    mergeFields,
    fitToContent,
    enableTextAlign,
    enableResize,
    bottomBarNode,
    initialHeight = 129,
    onFocus,
    onBlur,
    inModal = false,
    appendToSelector,
    dataQa,
  } = props;

  const placeholder = placeHolderProp ? placeHolderProp : defaultPlaceholder;
  const queryClient = useQueryClient();

  const mentionOptions = useMemo(() => {
    const options = {};

    if (inModal) {
      options.zIndex = layers.modals(1);
      options.appendToSelector = '.modal-body';
    } else if (appendToSelector) {
      options.appendToSelector = appendToSelector;
    }

    return { suggestion: getTeamMemberSuggestionOptions(queryClient, options) };
  }, [queryClient, inModal, appendToSelector]);

  return (
    <MaybeInputControl variant="outline" forInput {...props}>
      <BasicRichTextEditor
        enableAI
        enableMentions
        enableLinks
        autofocus={autoFocus}
        initialValue={value}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={onChange}
        fitToContent={fitToContent}
        enableMergeFields={mergeFields}
        enableTextAlign={enableTextAlign}
        initialHeight={initialHeight}
        enableResize={enableResize}
        defaultFonts={defaultFonts}
        bottomBarNode={bottomBarNode}
        onFocus={onFocus}
        onBlur={onBlur}
        ref={ref}
        mentionOptions={mentionOptions}
        enableToolbar={!disabled}
        dataQa={dataQa}
        {...props}
      />
    </MaybeInputControl>
  );
});
